import * as React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { EmptyState } from '@/components/EmptyState';
import { BatonEventRow } from '@/components/BatonEventRow';
import { Pagination } from '@/components/Pagination';
import { ApiError } from '@/lib/fetcher';
import {
  accountEventsKey,
  accountKey,
  fetchAccount,
  fetchAccountEvents,
} from '@/lib/queries';

/**
 * Overview — the account-at-a-glance page: the balance card, plus (brief
 * 115) a cross-baton activity log of the customer's baton events.
 */
export function Overview() {
  const accountQ = useQuery({ queryKey: accountKey, queryFn: fetchAccount });
  const [page, setPage] = React.useState({ limit: 20, offset: 0 });
  const eventsQ = useQuery({
    queryKey: accountEventsKey(page),
    queryFn: () => fetchAccountEvents(page),
  });
  const derivedTotal = eventsQ.data
    ? page.offset + eventsQ.data.data.length + (eventsQ.data.meta.hasMore ? 1 : 0)
    : 0;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
        <p className="text-muted-foreground">
          Your account balance and recent activity.
        </p>
      </header>

      <section className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Balance</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {accountQ.isPending ? (
              <Skeleton className="h-7 w-24" />
            ) : accountQ.error ? (
              <p className="text-sm text-destructive">
                {accountQ.error instanceof ApiError
                  ? accountQ.error.code
                  : 'Failed to load.'}
              </p>
            ) : accountQ.data ? (
              <>
                <div className="text-2xl font-semibold">
                  {accountQ.data.availableFormatted ??
                    accountQ.data.balanceFormatted ??
                    accountQ.data.balance}
                </div>
                <p className="text-xs text-muted-foreground">
                  Deposited:{' '}
                  {accountQ.data.balanceFormatted ?? accountQ.data.balance}
                </p>
              </>
            ) : null}
            <Button asChild variant="outline" size="sm" className="mt-3">
              <Link to="/dashboard/billing">Top up</Link>
            </Button>
          </CardContent>
        </Card>
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Recent activity</CardTitle>
          <CardDescription>Events across all your batons, newest first.</CardDescription>
        </CardHeader>
        <CardContent>
          {eventsQ.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : eventsQ.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load activity (
                {eventsQ.error instanceof ApiError ? eventsQ.error.code : 'unknown'})
              </AlertTitle>
            </Alert>
          ) : eventsQ.data && eventsQ.data.data.length === 0 && page.offset === 0 ? (
            <EmptyState
              icon={Activity}
              title="No activity yet"
              description="Create and use a baton — its events show up here."
            />
          ) : eventsQ.data ? (
            <>
              <ul>
                {eventsQ.data.data.map((e) => (
                  <BatonEventRow key={e.id} event={e} showBaton />
                ))}
              </ul>
              <div className="mt-3">
                <Pagination
                  total={derivedTotal}
                  limit={page.limit}
                  offset={page.offset}
                  onChange={setPage}
                />
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
